/**
 * Tipos para el módulo Transacciones de Recaudación (Pronto Pago) — Fase 6.2
 *
 * Documentos relacionados:
 *   - requerimientos.md § 6.2 (Webhook y conciliación)
 *   - DECISIONES.md #006 (cobranza manual), #027 (modelo webhook + simulador)
 *
 * Flujo:
 *   Pronto Pago → POST /webhooks/pronto-pago/rendicion
 *     → INSERT transacciones_recaudacion estado='pre_confirmada' (o 'sin_identificar')
 *     → Operador concilia desde la bandeja → RPC confirmar_transaccion
 *     → crea pago (via_pago=cvu_pronto_pago, estado=acreditado)
 *     → trigger pagos_after_change_update_cupon cierra el cupón
 */

export type EstadoTransaccion =
  | 'pre_confirmada'
  | 'acreditada'
  | 'rechazada'
  | 'sin_identificar';

export interface TransaccionRecaudacion {
  id: string;
  empresa_id: string | null;
  cvu_empresa_id: string | null;
  cuenta_corriente_id: string | null;
  monto: number;
  fecha_acreditacion: string;
  proveedor: string;
  /** Identificador único en Pronto Pago. UNIQUE en BD → idempotencia. */
  referencia_externa: string | null;
  canal: string;
  estado: EstadoTransaccion;
  raw_data: Record<string, unknown> | null;
  pago_id: string | null;
  confirmada_por: string | null;
  fecha_confirmacion: string | null;
  created_at: string;
  // Joins opcionales
  empresa?: { id: string; razon_social: string; cuit: string } | null;
  cvu_empresa?: { id: string; cvu: string; alias: string; name: string } | null;
  pago?: { id: string; cupon_id: string | null; monto_pagado: number } | null;
}

// ── Helpers de presentación ───────────────────────────────────────────────────

export function getEstadoTrxLabel(e: EstadoTransaccion | string): string {
  const labels: Record<string, string> = {
    pre_confirmada:    'Pre-confirmada',
    acreditada:        'Acreditada',
    rechazada:         'Rechazada',
    sin_identificar:   'Sin identificar',
  };
  return labels[e] ?? e;
}

export function getEstadoTrxColor(
  e: EstadoTransaccion | string,
): 'warning' | 'success' | 'error' | 'info' | 'default' {
  switch (e) {
    case 'pre_confirmada':   return 'warning';
    case 'acreditada':       return 'success';
    case 'rechazada':        return 'error';
    case 'sin_identificar':  return 'info';
    default:                 return 'default';
  }
}

// ── Payloads ──────────────────────────────────────────────────────────────────

export interface ConfirmarTrxPayload {
  id: string;
  cupon_id: string;
  /** Solo si la transacción está 'sin_identificar'. */
  empresa_id?: string;
}

export interface RechazarTrxPayload {
  id: string;
  motivo: string;
}

export interface ConfirmarTrxResult {
  transaccion_id: string;
  pago_id: string;
  cupon_id: string;
  empresa_id: string;
  monto: number;
}

// ── Simulador (dev) ───────────────────────────────────────────────────────────

export interface SimularWebhookPayload {
  referencia: string;
  externalId?: string;
  cvu?: string;
  monto: number;
  fechaAcreditacion?: string;
}

export interface SimularWebhookResult {
  simulated: true;
  webhook_status: number;
  webhook_response: {
    ok?: boolean;
    transaccion_id?: string;
    estado?: EstadoTransaccion;
    duplicated?: boolean;
    error?: string;
  };
}
